import * as React from "react";
import * as _ from "lodash";
import i18n from "i18n";
import { withStyles, Checkbox } from "@material-ui/core";
import { Warning as WarningIcon, Error as ErrorIcon } from "@material-ui/icons";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import { ModelMap } from "modules/modelMap/models/ModelMap";
import { ModelMapService } from "modules/modelMap/ModelMapService";
import { ModelMapStore } from "modules/modelMap/ModelMapStore";
import { TakeOffStore } from "modules/takeOff/TakeOffStore";
import { LibraryStore } from "modules/library/LibraryStore";
import { ProjectStore } from "modules/project/ProjectStore";
import ExclusionDialog from "../ExclusionDialog";
import EditableLabel from "./EditableLabel";
import {
  ActionButton,
  ExpandButton,
  ExpandedActionButtons,
} from "./ActionButtons";
import TakeOffList from "./TakeOffList";
import TakeOffErrorList from "./TakeOffErrorList";
import { gtag } from "ga-gtag";
import { EModelMapTab } from "common/enums";
import { WBSStore } from "modules/WBS/WBSStore";

export interface IModelMapView extends FirebaseDocument<ModelMap> {
  checked: boolean;
  saving: boolean;
  takeOffFormatedList: any[];
}

const styles = () => ({
  modelMapControls: {
    width: "99%",
    padding: 3,
    margin: "2px 1px",
    backgroundColor: "#363637",
    cursor: "pointer",
    "&:hover": {
      background: "#464646",
      "& > div > svg": {
        opacity: 1,
      },
    },
  },
  modelMapItemCheckAvailable: {
    display: "flex",
    "&:hover svg": {
      opacity: 1,
      cursor: "pointer",
    },
  },
  select: { padding: 5, color: "#a8a8a8", marginLeft: 4 },
  icons: {
    padding: 2,
    margin: "7px 5px 5px 5px",
    fontSize: 20,
  },
  actionContainer: { display: "flex", marginRight: "5px" },
});

type IProps = {
  classes: any;
  modelMap: IModelMapView;
  canEditName: boolean;
  showInsertTakeOffButton: boolean;
  showInsertQttButton: boolean;
  showActionButton: boolean;
};

const ModelMapItem = ({
  classes,
  modelMap,
  showInsertQttButton,
  showInsertTakeOffButton,
  canEditName,
  showActionButton,
}: IProps) => {
  const [hasAnyUpdate, setHasAnyUpdate] = React.useState(false);
  const [hasAnyError, setHasAnyError] = React.useState(false);
  const [openDelete, setOpenDelete] = React.useState(false);
  const [expand, setExpand] = React.useState(false);

  // componentDidCatch(error, info) {
  //   ExceptionStore.OpenException(info);
  // }

  React.useEffect(() => {
    ModelMapService.fetchModelMapsWbsNeedToUpdated(modelMap.id);
  }, []);

  /** Atualiza os status de erro e warning e os itens do takeOff */
  React.useEffect(() => {
    const newHasAnyError = checkIsAnyError();
    if (newHasAnyError !== hasAnyError) {
      setHasAnyError(newHasAnyError);
      refreshTakeOffList();
    }

    const newHasAnyUpdate = ModelMapService.hasCurrentWbsNeedToUpdatedModelMap(
      modelMap.id
    );
    if (newHasAnyUpdate !== hasAnyUpdate) {
      setHasAnyUpdate(newHasAnyUpdate);
      refreshTakeOffList();
    }
  }, [modelMap]);

  const resetSelected = () => {
    ModelMapStore.clearModelMapSelected();
  };

  const editModelMapName = async (newName: string) => {
    ModelMapStore.updateModelMapName(modelMap, newName);
  };

  const handleSelectModelMap = (event) => {
    ModelMapStore.setModelMapSelected(modelMap);
    ModelMapStore.toogleSelectedModelMap();
    event.stopPropagation();
  };

  const handleItemClick = (event) => {
    ModelMapStore.setModelMapSelected(modelMap);
    event.stopPropagation();
  };

  const handleDeleteModelMap = async () => {
    gtag("event", "deletar_model_map", {
      deletar_model_map: 0,
    });

    await ModelMapStore.deleteModelMap();
  };

  const editModelMap = () => {
    ModelMapStore.setModelMapSelected(modelMap);
    const {
      hasFormatError,
      hasFilterError,
      hasModelErrors,
      hasCategoryErrors,
    } = getModelMapErrors();

    if (
      isLibrariesEmpty(
        hasFormatError || hasFilterError || hasModelErrors || hasCategoryErrors
      )
    ) {
      return;
    }

    setTimeout(() => {
      ModelMapStore.openTakeOff();
      setTimeout(() => {
        if (hasModelErrors) {
          TakeOffStore.setSwipeTab(EModelMapTab.CONFIGURATION);
        } else if (hasCategoryErrors) {
          TakeOffStore.setSwipeTab(EModelMapTab.CONFIGURATION);
        } else if (hasFormatError) {
          TakeOffStore.setSwipeTab(EModelMapTab.FILTER);
        } else if (hasFilterError) {
          TakeOffStore.setSwipeTab(EModelMapTab.FORMATING);
        }
      }, 1000);
    }, 200);
  };

  const duplicateModelMap = async () => {
    gtag("event", "duplicar_model_map", {
      duplicar_model_map: 0,
    });
    await ModelMapStore.duplicateModelMap(modelMap.id, modelMap.data.name);
  };

  const handleClickLineError = async (modelMap, isCategoryOrModel, filter) => {
    if (isLibrariesEmpty(true)) {
      return;
    }

    await ModelMapStore.openTakeOffToFixErrors(
      modelMap,
      isCategoryOrModel,
      filter
    );
  };

  const isLibrariesEmpty = (error): boolean => {
    if (LibraryStore.libraries.length == 0) {
      if (!ProjectStore.showSnack) {
        let secondMsg = error
          ? i18n.modelMaps.menuLateral.noModel3d2error
          : i18n.modelMaps.menuLateral.noModel3d2;
        ProjectStore.showSnackMessage(
          i18n.modelMaps.menuLateral.noModel3d,
          secondMsg
        );
      }
      return true;
    }
    return false;
  };

  const handleClickExpand = (newExpand) => {
    if (newExpand) {
      refreshTakeOffList();
    }
    setExpand(newExpand);
  };

  const handleClickAction = (action) => {
    if (action === "edit") {
      editModelMap();
      return;
    }

    if (action === "delete") {
      setOpenDelete(true);
      return;
    }

    if (action === "duplicate") {
      duplicateModelMap();
      return;
    }

    if (action === "refresh") {
      ModelMapStore.updateTakeOffIntoWBS(modelMap);
      return;
    }

    if (action === "insert") {
      ModelMapStore.insertTakeOffIntoWBS(modelMap);
      return;
    }
  };

  const handleClickLine = (line) => {
    const re = /(\\d*|[+*/()-]|np\\d+|\\s+)/;
    const res = re.test(WBSStore.selectedLine.data.formula);
    if (
      WBSStore.selectedLine.data.formula == "" ||
      WBSStore.selectedLine.data.formula == undefined ||
      res === false
    ) {
      ModelMapStore.addQuantityFromTakeOffItem(line);
    } else {
      WBSStore.changeFunction(true);
      WBSStore.changeTempLineAddQuantity(line);
      return;
    }
    // ModelMapStore.addQuantityFromTakeOffItem(line);
  };

  async function refreshTakeOffList() {
    await TakeOffStore.fetchTakeOffFormatedList(modelMap, undefined, false);
  }

  function loadTakeOffList() {
    const list: any[] = [];
    let oldList: string[] = [];
    let stageList: string[] = [];

    for (const element of modelMap.takeOffFormatedList) {
      if (element.index == 0) {
        stageList = [];
      }

      if (element.isStage) {
        stageList.splice(
          element.parentIndex + 1,
          stageList.length - element.parentIndex + 1
        );
        stageList.push(element.description);
      } else {
        if (!_.isEqual(oldList, stageList)) {
          list.push({
            isStage: true,
            description: stageList
              .map((s) => s)
              .join(" ‏‏‎  ‏‏‎  ‏‏‎ > ‏‏‎  ‏‏‎  ‏‏‎ "),
          });
          oldList = [...stageList];
        }
        list.push({ ...element, description: element.description });
      }
    }

    return list;
  }

  function checkIsAnyError(): boolean {
    const {
      hasFormatError,
      hasFilterError,
      hasModelErrors,
      hasCategoryErrors,
    } = getModelMapErrors();

    return Boolean(
      hasFormatError || hasFilterError || hasModelErrors || hasCategoryErrors
    );
  }

  function getModelMapErrors() {
    const { formatErrors, filterErrors, modelErrors, categoryErrors } =
      modelMap.data;

    return {
      hasFormatError: formatErrors && formatErrors.length > 0,
      hasFilterError: filterErrors && filterErrors.length > 0,
      hasModelErrors: modelErrors && modelErrors.length > 0,
      hasCategoryErrors: categoryErrors && categoryErrors.length > 0,
    };
  }

  return (
    <div>
      <div
        id={"divMM" + modelMap.id}
        className={classes.modelMapControls}
        onClick={handleItemClick}
      >
        <div className={classes.modelMapItemCheckAvailable}>
          <Checkbox
            className={classes.select}
            checked={modelMap.checked}
            onClick={handleSelectModelMap}
            inputProps={{
              "aria-label": "uncontrolled-checkbox",
            }}
          />
          {hasAnyError && (
            <ErrorIcon
              className={classes.icons}
              style={{
                color: "#FB4436",
              }}
            />
          )}
          {!hasAnyError && hasAnyUpdate && (
            <WarningIcon
              className={classes.icons}
              style={{
                color: "#FFF700",
              }}
            />
          )}
          <EditableLabel
            label={i18n.rateLibraries.name}
            value={modelMap.data.name}
            canEdit={canEditName}
            margin={Boolean(hasAnyUpdate || hasAnyError)}
            onEnableEdit={resetSelected}
            onInputChange={resetSelected}
            onInputBlur={editModelMapName}
          />
          <div className={classes.actionContainer}>
            {showActionButton && !modelMap.saving && !expand && (
              <ActionButton
                hasAnyUpdate={hasAnyUpdate}
                hasAnyError={hasAnyError}
                canInsertTakeOff={showInsertTakeOffButton}
                onClick={handleClickAction}
              />
            )}
            {/* isNotATask /* PRUBK-801: Verifica se a linha selecionada é uma task(intem de composição), caso não seja ele exibe o botão expandir MP */}
            <ExpandButton expand={expand} onChange={handleClickExpand} />
          </div>
        </div>
        {expand && (
          <ExpandedActionButtons
            hasAnyUpdate={hasAnyUpdate}
            hasAnyError={hasAnyError}
            canInsertTakeOff={showInsertTakeOffButton}
            onClick={handleClickAction}
          />
        )}
      </div>
      <ExclusionDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDeleteModelMap}
      />

      {expand && !hasAnyError && (
        <TakeOffList
          takeOffFormatedList={loadTakeOffList()}
          canInsertAtEAP={showInsertQttButton}
          hasNoErrors={
            !modelMap.data.filterErrors &&
            !modelMap.data.formatErrors &&
            !modelMap.data.modelErrors &&
            !modelMap.data.categoryErrors
          }
          onLineClick={handleClickLine}
        />
      )}

      {expand && hasAnyError && (
        <TakeOffErrorList modelMap={modelMap} onClick={handleClickLineError} />
      )}
    </div>
  );
};

export default withStyles(styles)(ModelMapItem);



// WEBPACK FOOTER //
// ./src/modules/modelMap/components/layouts/ModelMapItem/index.tsx